export const PrivacyData = {
  title: 'Privacy Policy',
  lastUpdated: 'Last Updated: October 15, 2025',
  importantNotice: 'Nobilis Global, LLC. ("Nobilis," "we," "us," or "our") is committed to protecting the privacy and security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our exclusive social networking platform, website, mobile application, and related services (collectively, the "Platform"). By accessing or using the Platform, you consent to the data practices described in this Privacy Policy. IMPORTANT: We understand that our Members include high-profile individuals who require enhanced privacy protections. We have implemented stringent security measures and strict internal policies to safeguard your personal information and maintain the confidentiality of our exclusive community.',
  sections: [
    {
      title: '1. INFORMATION WE COLLECT',
      paragraphs: [
        'We collect several types of information from and about users of our Platform:'
      ],
      subsections: [
        {
          subtitle: '1.1 Information You Provide Directly',
          paragraphs: [
            'Account Registration Information: When you apply for membership or create an account, we collect:'
          ],
          bullets: [
            'Full legal name and any professional or stage names',
            'Email address and telephone number',
            'Professional information (occupation, employer, achievements, credentials)',
            'Date of birth and nationality',
            'Social media profiles and professional references',
            'Profile photograph and biographical information'
          ],
          additionalParagraphs: [
            'Verification Information: To maintain the exclusivity and authenticity of our community, we may collect:'
          ],
          additionalBullets: [
            'Government-issued identification documents',
            'Professional certifications or licenses',
            'Publicly available information about your achievements and reputation'
          ]
        },
        {
          paragraphs: [
            'Content and Communications: We collect information you provide when using the Platform, including:'
          ],
          bullets: [
            'Messages, posts, and comments you share with other Members',
            'Content shared in Mastermind Circles',
            'Experience listings, bookings, and reviews',
            'Communications with our support team'
          ],
          additionalParagraphs: [
            'Payment Information: When you make purchases or bookings through the Platform:'
          ],
          additionalBullets: [
            'Billing address and payment card information (processed securely through third-party payment processors)',
            'Transaction history and receipts'
          ]
        },
        {
          paragraphs: [
            'Financial Documentation: For wealth-based qualification, bank statements, investment account statements, real estate appraisals, business valuations, trust documents, tax returns, and other documentation demonstrating net worth of at least $30,000,000 USD',
            'Third-Party Verifications: Information obtained from financial institutions, accountants, attorneys, and other professionals to verify your eligibility'
          ]
        },
        {
          subtitle: '1.2 Information Collected Automatically',
          paragraphs: [
            'When you access or use the Platform, we automatically collect certain information:'
          ],
          bullets: [
            'Device Information: Hardware model, operating system, unique device identifiers, mobile network information',
            'Log Information: IP address, browser type, pages visited, time and date of access, time spent on pages, referral sources',
            'Location Information: General geographic location based on IP address; precise location if you enable location services',
            'Cookies and Tracking Technologies: Session cookies, persistent cookies, web beacons, and similar technologies to enhance your experience'
          ]
        },
        {
          subtitle: '1.3 Information from Third Parties',
          paragraphs: [
            'We may receive information about you from third-party sources, including:'
          ],
          bullets: [
            'Members who refer or nominate you for membership',
            'Professional verification services and background check providers',
            'Public databases and directories (e.g., professional registries, news articles, social media)',
            'Social media platforms if you choose to link your accounts'
          ]
        }
      ]
    },
    {
      title: '2. HOW WE USE YOUR INFORMATION',
      paragraphs: [
        'We use the information we collect for the following purposes:'
      ],
      subsections: [
        {
          subtitle: '2.1 Platform Operation and Services',
          bullets: [
            'Create, maintain, and manage your account',
            'Verify your identity and evaluate membership applications',
            'Facilitate networking, Mastermind circles, and Experiences, Expertise',
            'Process payments and transactions',
            'Provide customer support and respond to inquiries'
          ]
        },
        {
          subtitle: '2.2 Platform Improvement and Personalization',
          bullets: [
            'Analyze usage patterns to improve Platform functionality and user experience',
            'Personalize content, recommendations, and connections based on your interests and activity',
            'Develop new features, products, and services'
          ]
        },
        {
          subtitle: '2.3 Communication',
          bullets: [
            'Send transactional emails (e.g., confirmations, receipts, account notifications)',
            'Notify you about new features, events, or opportunities relevant to your interests',
            'Conduct surveys and gather feedback (you may opt out)'
          ]
        },
        {
          subtitle: '2.4 Safety, Security, and Compliance',
          bullets: [
            'Detect, prevent, and respond to fraud, abuse, security incidents, and other harmful activities',
            'Enforce our Terms of Service and other policies',
            'Comply with legal obligations, court orders, and regulatory requirements',
            'Protect the rights, property, and safety of Nobilis, Members, and the public'
          ]
        },
        {
          subtitle: '2.5 Legal Basis for Processing (GDPR)',
          paragraphs: [
            'For users in the European Economic Area (EEA), UK, and Switzerland, we process your personal data based on:'
          ],
          bullets: [
            'Contractual Necessity: Processing necessary to fulfill our contract with you (e.g., providing Platform services)',
            'Legitimate Interests: Processing for our legitimate business interests (e.g., improving the Platform, security), provided your rights do not override these interests',
            'Consent: Where you have provided explicit consent (e.g., marketing communications)',
            'Legal Obligation: Processing required by law (e.g., tax reporting, compliance with court orders)'
          ]
        }
      ]
    },
    {
      title: '3. HOW WE SHARE YOUR INFORMATION',
      paragraphs: [
        'We value your privacy and do not sell your personal information to third parties. We may share your information in the following circumstances:'
      ],
      subsections: [
        {
          subtitle: '3.1 With Other Members and Partners',
          paragraphs: [
            'Certain profile information (name, photograph, professional background) is visible to other Members to facilitate networking. You may control the visibility of additional information through your privacy settings or by inviting or being invited other Members to private events (Mastermind Circles or Experiences), then . Content you share in Mastermind Groups and Private Experiences (no matter you host or you are invited) is visible only to the invited Members, while non hidden profile and Experiences (public) are visible to all Nobilis Community Members.',
            'Partners have access only to the content that they create or where they are invited, all other information is not visible to Partners. and Open Discussions is visible to other group or community members. Experience hosts and guests can see each other\'s basic profile information.'
          ]
        },
        {
          subtitle: '3.2 With Service Providers',
          paragraphs: [
            'We engage trusted third-party service providers to perform functions on our behalf, such as:'
          ],
          bullets: [
            'Cloud hosting and data storage',
            'Payment processing',
            'Identity verification and background checks',
            'Email delivery and customer support',
            'Analytics and performance monitoring'
          ],
          additionalParagraphs: [
            'These service providers are contractually bound to protect your information and use it only for the purposes we specify.'
          ]
        },
        {
          subtitle: '3.3 For Legal and Safety Reasons',
          paragraphs: [
            'We may disclose your information if required or permitted by law, including to:'
          ],
          bullets: [
            'Comply with legal obligations, court orders, subpoenas, or government requests',
            'Enforce our Terms of Service and other agreements',
            'Detect, prevent, or address fraud, security, or technical issues',
            'Protect the rights, property, or safety of Nobilis, Members, or others'
          ]
        },
        {
          subtitle: '3.4 Business Transfers',
          paragraphs: [
            'In the event of a merger, acquisition, reorganization, bankruptcy, or sale of assets, your information may be transferred to the successor entity. We will provide notice before your information is transferred and becomes subject to a different privacy policy.'
          ]
        },
        {
          subtitle: '3.5 With Your Consent',
          paragraphs: [
            'We may share your information with third parties when you explicitly consent or direct us to do so.'
          ]
        }
      ]
    },
    {
      title: '4. DATA RETENTION',
      paragraphs: [
        'We retain your personal information for as long as necessary to provide the Platform services, comply with legal obligations, resolve disputes, and enforce our agreements. When you close your account, we will delete or anonymize your information within a reasonable timeframe, except where we are required to retain it for legal, regulatory, or legitimate business purposes (e.g., fraud prevention, financial records). Backup copies may persist for a limited period before permanent deletion.'
      ]
    },
    {
      title: '5. DATA SECURITY',
      paragraphs: [
        'We implement robust technical, administrative, and physical security measures to protect your information from unauthorized access, use, disclosure, alteration, or destruction. Our security practices include:'
      ],
      bullets: [
        'Encryption of data in transit and at rest using industry-standard protocols',
        'Secure data centers with restricted physical access',
        'Regular security audits and vulnerability assessments',
        'Multi-factor authentication for sensitive operations',
        'Strict access controls and employee confidentiality agreements',
        'Incident response and breach notification procedures'
      ],
      additionalParagraphs: [
        'However, no method of electronic transmission or storage is completely secure. While we strive to protect your information, we cannot guarantee absolute security. You are responsible for maintaining the confidentiality of your account credentials.'
      ]
    },
    {
      title: '6. YOUR PRIVACY RIGHTS AND CHOICES',
      paragraphs: [
        'Depending on your location, you may have the following rights regarding your personal information:'
      ],
      subsections: [
        {
          subtitle: '6.1 Access and Portability',
          paragraphs: [
            'You have the right to access your personal information and request a copy in a portable format. You can access much of your information directly through your account settings.'
          ]
        },
        {
          subtitle: '6.2 Correction and Update',
          paragraphs: [
            'You have the right to correct inaccurate or incomplete information. You can update most information through your account settings or by contacting us.'
          ]
        },
        {
          subtitle: '6.3 Deletion',
          paragraphs: [
            'You have the right to request deletion of your personal information, subject to certain exceptions (e.g., legal obligations, ongoing disputes). You can close your account at any time through account settings or by contacting us.'
          ]
        },
        {
          subtitle: '6.4 Restriction and Objection',
          paragraphs: [
            'You have the right to restrict or object to certain processing activities, particularly for direct marketing purposes. You can opt out of marketing communications at any time by using the unsubscribe link in emails or adjusting your notification preferences.'
          ]
        },
        {
          subtitle: '6.5 Withdraw Consent',
          paragraphs: [
            'Where we process your information based on consent, you have the right to withdraw that consent at any time. This will not affect the lawfulness of processing before the withdrawal.'
          ]
        },
        {
          subtitle: '6.6 California Privacy Rights (CCPA)',
          paragraphs: [
            'California residents have additional rights under the California Consumer Privacy Act (CCPA):'
          ],
          bullets: [
            'Right to know what personal information we collect, use, disclose, and sell',
            'Right to request deletion of personal information',
            'Right to opt out of the sale of personal information (we do not sell personal information)',
            'Right to non-discrimination for exercising your privacy rights'
          ]
        },
        {
          subtitle: '6.7 European Privacy Rights (GDPR)',
          paragraphs: [
            'If you are located in the EEA, UK, or Switzerland, you have rights under the General Data Protection Regulation (GDPR):'
          ],
          bullets: [
            'Right of access, rectification, erasure, and data portability',
            'Right to restrict or object to processing',
            'Right to withdraw consent',
            'Right to lodge a complaint with your local data protection authority'
          ]
        },
        {
          subtitle: '6.8 How to Exercise Your Rights',
          paragraphs: [
            'To exercise any of these rights, please contact us at privacy@joinnobilis.com or use the contact information provided in Section 12. We will respond to your request within the timeframes required by applicable law. We may need to verify your identity before processing your request.'
          ]
        }
      ]
    },
    {
      title: '7. COOKIES AND TRACKING TECHNOLOGIES',
      paragraphs: [
        'We use cookies, web beacons, and similar technologies to enhance your experience, analyze Platform usage, and deliver personalized content. Types of technologies we use include:'
      ],
      bullets: [
        'Essential Cookies: Necessary for Platform functionality (e.g., authentication, security)',
        'Performance Cookies: Help us understand how you use the Platform and identify areas for improvement',
        'Functional Cookies: Remember your preferences and settings',
        'Advertising Cookies: Deliver relevant content and measure campaign effectiveness'
      ],
      additionalParagraphs: [
        'You can control cookies through your browser settings. However, disabling certain cookies may limit your ability to use some Platform features. For more information about cookies, visit www.allaboutcookies.org.'
      ]
    },
    {
      title: '8. INTERNATIONAL DATA TRANSFERS',
      paragraphs: [
        'Nobilis is based in the United States, and our servers and service providers are primarily located in the United States. If you access the Platform from outside the United States, your information will be transferred to, stored, and processed in the United States and other countries where data protection laws may differ from those in your jurisdiction. By using the Platform, you consent to the transfer of your information to the United States and other countries. For transfers from the EEA, UK, or Switzerland, we implement appropriate safeguards such as Standard Contractual Clauses approved by the European Commission.'
      ]
    },
    {
      title: '9. CHILDREN\'S PRIVACY',
      paragraphs: [
        'The Platform is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have inadvertently collected information from a child under 18, we will take steps to delete such information promptly. If you believe we have collected information from a child, please contact us immediately.'
      ]
    },
    {
      title: '10. THIRD-PARTY LINKS AND SERVICES',
      paragraphs: [
        'The Platform may contain links to third-party websites, services, or applications that are not operated by Nobilis. This Privacy Policy does not apply to third-party services, and we are not responsible for their privacy practices. We encourage you to review the privacy policies of any third-party services you access through the Platform.'
      ]
    },
    {
      title: '11. CHANGES TO THIS PRIVACY POLICY',
      paragraphs: [
        'We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or Platform features. We will notify you of material changes by posting the updated Privacy Policy on the Platform and updating the effective date. Your continued use of the Platform after changes are posted constitutes your acceptance of the revised Privacy Policy. We encourage you to review this Privacy Policy periodically.'
      ]
    },
    {
      title: '12. CONTACT INFORMATION',
      paragraphs: [
        'If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:',
        'Nobilis Global, LLC.',
        'Privacy Officer',
        'Email: privacy@joinnobilis.com',
        'Website: www.joinnobilis.com',
        'Address: 10900 Research Blvd, Suite 160C #1119, Austin, TX 78759',
        'For users in the European Economic Area, UK, or Switzerland, you may also contact our Data Protection Officer at dpo@joinnobilis.com.'
      ]
    },
    {
      title: '13. DATA PROTECTION AUTHORITY',
      paragraphs: [
        'If you are located in the EEA, UK, or Switzerland and believe we have not adequately addressed your concerns, you have the right to lodge a complaint with your local data protection supervisory authority.'
      ]
    }
  ],
  closingStatement: 'BY USING THE PLATFORM, YOU ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTOOD THIS PRIVACY POLICY AND CONSENT TO THE COLLECTION, USE, AND DISCLOSURE OF YOUR INFORMATION AS DESCRIBED HEREIN.'
}